<template>
  <div class="paper-editor-wrapper">
    <!-- 상단 헤더 -->
    <div class="paper-editor-header">
      <div class="d-flex align-center">
        <h6 class="text-subtitle-1 font-weight-bold mr-4">논문 편집기</h6>
        <v-select
          v-model="selectedPaperIdx"
          :items="papers"
          :item-text="(item) => item.title"
          :item-value="(item) => item.idx"
          label="논문 선택"
          outlined
          dense
          hide-details
          style="min-width: 300px; max-width: 400px;"
          @change="handlePaperChange"
        >
          <template v-slot:prepend-item>
            <v-list-item @click="openGroupSelector">
              <v-list-item-icon>
                <v-icon>mdi-folder-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="grey--text">
                  {{ selectedGroup ? selectedGroup.title : '그룹 선택...' }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon small>mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
            <v-divider class="mt-2"></v-divider>
          </template>
        </v-select>
      </div>

      <div class="d-flex align-center">
        <v-btn
          color="primary"
          small
          class="mr-2"
          :loading="isSaving"
          :disabled="!selectedPaper"
          @click="savePaper"
        >
          <v-icon left size="16">mdi-content-save</v-icon>
          저장
        </v-btn>

        <v-btn
          color="grey darken-2"
          small
          outlined
          :disabled="!selectedPaper"
          @click="exportPdf"
        >
          <v-icon left size="16">mdi-file-pdf-box</v-icon>
          PDF
        </v-btn>

        <v-btn
          color="grey darken-2"
          small
          outlined
          class="ml-2"
          :loading="isFullPdfLoading"
          :disabled="groups.length === 0"
          @click="exportFullJournalPdf"
        >
          <v-icon left size="16">mdi-file-document-multiple</v-icon>
          전체 PDF
        </v-btn>
      </div>
    </div>

    <!-- 메인 컨텐츠 영역 -->
    <div class="paper-editor-body">
      <!-- 논문 미선택 상태 -->
      <div v-if="!selectedPaper" class="empty-state">
        <v-icon size="64" color="grey lighten-1">mdi-file-document-edit-outline</v-icon>
        <p class="text-body-1 grey--text mt-4">
          논문을 선택하면 편집기가 표시됩니다
        </p>
        <v-btn
          v-if="groups.length > 0"
          color="primary"
          outlined
          class="mt-2"
          @click="openGroupSelector"
        >
          <v-icon left size="18">mdi-folder-open</v-icon>
          그룹에서 논문 선택
        </v-btn>
      </div>

      <!-- 편집기 영역 -->
      <template v-else>
        <!-- 좌측: 편집 패널 -->
        <div class="editor-panel">
          <!-- 논문 정보 -->
          <div class="paper-info-bar">
            <h2 class="text-subtitle-2 font-weight-medium text-truncate">
              {{ selectedPaper.title }}
            </h2>
            <p v-if="selectedPaper.authors && selectedPaper.authors.length" class="text-caption grey--text mb-0 text-truncate">
              {{ selectedPaper.authors.map(a => a.full_name).join(', ') }}
            </p>
          </div>

          <!-- 툴바 -->
          <div class="editor-toolbar">
            <v-btn x-small text @click="addBlock('heading')">
              <v-icon size="14">mdi-format-header-2</v-icon>
              <span class="ml-1 text-caption">제목</span>
            </v-btn>
            <v-btn x-small text @click="addBlock('paragraph')">
              <v-icon size="14">mdi-format-paragraph</v-icon>
              <span class="ml-1 text-caption">문단</span>
            </v-btn>
            <v-btn x-small text @click="addBlock('blockquote', { variant: 1 })">
              <v-icon size="14">mdi-format-quote-close</v-icon>
              <span class="ml-1 text-caption">인용1</span>
            </v-btn>
            <v-btn x-small text @click="addBlock('blockquote', { variant: 2 })">
              <v-icon size="14">mdi-format-quote-close</v-icon>
              <span class="ml-1 text-caption">인용2</span>
            </v-btn>
            <v-btn x-small text @click="addBlock('blockquote', { variant: 3 })">
              <v-icon size="14">mdi-format-quote-close</v-icon>
              <span class="ml-1 text-caption">인용3</span>
            </v-btn>
            <v-btn x-small text @click="addBlock('blockquote', { variant: 4 })">
              <v-icon size="14">mdi-format-quote-close</v-icon>
              <span class="ml-1 text-caption">인용4</span>
            </v-btn>
            <v-btn x-small text @click="addBlock('table')">
              <v-icon size="14">mdi-table</v-icon>
              <span class="ml-1 text-caption">표</span>
            </v-btn>
            <v-btn x-small text @click="addBlock('figure')">
              <v-icon size="14">mdi-image-outline</v-icon>
              <span class="ml-1 text-caption">그림</span>
            </v-btn>
            <v-btn x-small text @click="addBlock('page_break')">
              <v-icon size="14">mdi-format-page-break</v-icon>
              <span class="ml-1 text-caption">페이지분할</span>
            </v-btn>
            <v-btn x-small text @click="addBlock('references')">
              <v-icon size="14">mdi-book-open-page-variant</v-icon>
              <span class="ml-1 text-caption">참고문헌</span>
            </v-btn>
          </div>

          <!-- 블록 에디터 -->
          <div class="editor-blocks">
            <div
              v-for="(block, index) in ast.blocks"
              :key="`block-${index}`"
              class="editor-block"
              :class="{ 'editor-block-active': index === selectedBlockIndex }"
              @click="selectedBlockIndex = index"
            >
              <div class="block-header">
                <span class="block-type">{{ block.type }}</span>
                <v-btn x-small icon @click.stop="removeBlock(index)">
                  <v-icon size="14">mdi-close</v-icon>
                </v-btn>
              </div>

              <div v-if="block.type === 'heading'" class="block-body">
                <v-select
                  v-model="block.level"
                  :items="[2,3,4,5]"
                  dense
                  hide-details
                  label="레벨"
                  class="mr-2"
                  style="max-width: 120px"
                ></v-select>
                <v-text-field v-model="block.text" dense hide-details label="제목"></v-text-field>
              </div>

              <div v-else-if="block.type === 'paragraph'" class="block-body">
                <v-textarea v-model="block.text" dense hide-details rows="3" label="문단"></v-textarea>
                <div class="block-actions">
                  <v-btn x-small text @click="insertFootnote(block)">
                    <v-icon size="14">mdi-numeric-1-box-outline</v-icon>
                    <span class="ml-1 text-caption">각주</span>
                  </v-btn>
                </div>
              </div>

              <div v-else-if="block.type === 'blockquote'" class="block-body">
                <v-select
                  v-model="block.variant"
                  :items="[1, 2, 3, 4]"
                  dense
                  hide-details
                  label="스타일"
                  style="max-width: 140px"
                ></v-select>
                <v-text-field v-model="block.title" dense hide-details label="제목(선택)"></v-text-field>
                <v-textarea v-model="block.text" dense hide-details rows="3" label="인용"></v-textarea>
              </div>

              <div v-else-if="block.type === 'table'" class="block-body">
                <v-text-field v-model="block.caption" dense hide-details label="표 캡션"></v-text-field>
                <div class="table-settings">
                  <v-text-field
                    v-model.number="block.width"
                    dense
                    hide-details
                    type="number"
                    label="폭(%)"
                    style="max-width: 120px"
                  ></v-text-field>
                  <v-select
                    v-model="block.layout"
                    :items="['fixed', 'auto']"
                    dense
                    hide-details
                    label="레이아웃"
                    style="max-width: 140px"
                  ></v-select>
                  <v-text-field
                    v-model.number="block.cellPaddingY"
                    dense
                    hide-details
                    type="number"
                    label="세로 패딩(px)"
                    style="max-width: 160px"
                  ></v-text-field>
                  <v-text-field
                    v-model.number="block.cellPaddingX"
                    dense
                    hide-details
                    type="number"
                    label="가로 패딩(px)"
                    style="max-width: 160px"
                  ></v-text-field>
                  <v-text-field
                    v-model.number="block.lineHeight"
                    dense
                    hide-details
                    type="number"
                    label="행높이(pt)"
                    style="max-width: 150px"
                  ></v-text-field>
                  <v-text-field
                    v-model="block.columnWidths"
                    dense
                    hide-details
                    label="열 폭(%, 콤마 구분)"
                    style="min-width: 220px"
                  ></v-text-field>
                </div>
                <v-textarea
                  v-model="block.rowsText"
                  dense
                  hide-details
                  rows="4"
                  label="행 입력 (| 로 구분)"
                ></v-textarea>
              </div>

              <div v-else-if="block.type === 'figure'" class="block-body">
                <v-text-field v-model="block.src" dense hide-details label="이미지 경로"></v-text-field>
                <v-text-field v-model="block.caption" dense hide-details label="그림 캡션"></v-text-field>
                <div class="figure-settings">
                  <v-text-field
                    v-model.number="block.width"
                    dense
                    hide-details
                    type="number"
                    label="폭(px)"
                    style="max-width: 140px"
                  ></v-text-field>
                  <v-text-field
                    v-model.number="block.height"
                    dense
                    hide-details
                    type="number"
                    label="높이(px)"
                    style="max-width: 140px"
                  ></v-text-field>
                </div>
              </div>

              <div v-else-if="block.type === 'page_break'" class="block-body block-body-center">
                <span class="text-caption grey--text">페이지 나누기</span>
              </div>

              <div v-else-if="block.type === 'references'" class="block-body">
                <v-textarea
                  v-model="block.itemsText"
                  dense
                  hide-details
                  rows="4"
                  label="참고문헌 (줄바꿈=항목)"
                ></v-textarea>
              </div>

              <div v-else-if="block.type === 'raw_html'" class="block-body">
                <v-textarea v-model="block.html" dense hide-details rows="4" label="원본 HTML"></v-textarea>
              </div>
            </div>

            <div v-if="ast.blocks.length === 0" class="editor-empty">
              블록을 추가해 내용을 작성하세요.
            </div>
          </div>
        </div>

        <!-- 우측: 미리보기 패널 -->
        <div class="preview-panel final-preview-panel">
          <!-- 미리보기 헤더 -->
          <div class="preview-header">
            <span class="text-subtitle-2 font-weight-medium">미리보기</span>
            <span class="text-caption grey--text">{{ totalPageCount }}페이지</span>
          </div>

          <!-- 페이지 미리보기 영역 -->
          <div class="preview-content final-preview-panel" ref="previewContent">
            <full-journal-preview-content
              v-if="selectedPaper"
              :journal="journal"
              :page-size="effectivePageSize"
              :pages="singlePaperPages"
              :items="papers"
              :enabled-fixed-pages="isSingleFixedPageEnabled"
              :scale="previewScale"
            />
          </div>
        </div>

        <!-- PDF 출력용 (전체 레이아웃) -->
        <div ref="fullPdfContent" class="pdf-capture-container">
          <full-journal-preview-content
            v-if="selectedPaper"
            :journal="journal"
            :page-size="effectivePageSize"
            :pages="singlePaperPages"
            :items="papers"
            :enabled-fixed-pages="isSingleFixedPageEnabled"
          />
        </div>

      </template>
    </div>

    <!-- PDF 출력용 (저널 전체 레이아웃) -->
    <div ref="fullJournalPdfContent" class="pdf-capture-container">
      <full-journal-preview-content
        v-if="fullPdfPages.length > 0"
        :journal="journal"
        :page-size="effectivePageSize"
        :pages="fullPdfPages"
        :items="fullPdfItems"
        :enabled-fixed-pages="isFixedPageEnabled"
      />
    </div>

    <!-- 그룹 선택 다이얼로그 -->
    <v-dialog v-model="showGroupDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="text-h6">그룹 선택</span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <v-list v-if="groups.length > 0">
            <v-list-item
              v-for="group in groups"
              :key="group.idx"
              @click="selectGroup(group)"
              :class="{ 'primary lighten-5': selectedGroup?.idx === group.idx }"
            >
              <v-list-item-icon>
                <v-icon>mdi-folder</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ group.title }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="selectedGroup?.idx === group.idx">
                <v-icon color="primary">mdi-check</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <div v-else class="text-center pa-6">
            <v-icon size="48" color="grey lighten-1">mdi-folder-off-outline</v-icon>
            <p class="grey--text mt-2">등록된 그룹이 없습니다.</p>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showGroupDialog = false">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/* eslint-disable no-console */
import { journalContentGroupApi, journalContentApi, journalApi } from "@/api/api";
import {
  PAGE_DIMENSIONS,
  getContentWidth,
  getPagePadding,
  splitContentByHeight,
} from "@/utils/paperEditor";
import { openIframePrintPreview } from "@/utils/pdfGenerator";
import { parseContentToAst, renderAstToHtml } from "@/utils/paperAst";
import FullJournalPreviewContent from "@/components/Journal/FullJournalPreviewContent.vue";

const SERVER_URL = process.env.VUE_APP_SERVER_URI;

export default {
  name: "edit-paper-pages-v2",
  components: {
    FullJournalPreviewContent,
  },
  props: {
    journal_idx: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      journal: {},
      groups: [],
      papers: [],
      selectedGroup: null,
      selectedPaperIdx: null,
      selectedPaper: null,
      ast: { version: 1, blocks: [] },
      pageSize: "A5",
      previewScale: 0.5,
      showGroupDialog: false,
      selectedBlockIndex: -1,
      fullPdfGroups: [],
      fullPdfPages: [],
      isFullPdfLoading: false,
    };
  },
  computed: {
    effectivePageSize() {
      const candidate = this.journal?.page_size;
      return PAGE_DIMENSIONS[candidate] ? candidate : this.pageSize;
    },
    dimensions() {
      return PAGE_DIMENSIONS[this.effectivePageSize];
    },
    abstractSource() {
      if (!this.selectedPaper) return "";
      return this.selectedPaper.abstract || this.selectedPaper.abstract_eng || "";
    },
    abstractHtml() {
      if (!this.abstractSource) return "";
      return this.processImageUrls(
        renderAstToHtml(parseContentToAst(this.abstractSource))
      );
    },
    abstractPages() {
      if (!this.abstractHtml) return [""];
      return splitContentByHeight(
        this.abstractHtml,
        this.effectivePageSize,
        this.getAbstractCoverHeight(),
        {
          containerStyle: 'font-family: "KoPubWorldBatang", "Noto Serif KR", serif; font-size: 10pt; line-height: 1.8; text-align: justify;',
          paragraphStyle: "margin: 0 0 10px 0;",
        }
      );
    },
    contentStartPage() {
      return 2 + this.abstractPages.length;
    },
    // 본문 HTML 변환
    htmlContent() {
      return this.processImageUrls(renderAstToHtml(this.ast));
    },
    // 본문 페이지 분할
    contentPages() {
      if (!this.htmlContent) return [""];
      return splitContentByHeight(this.htmlContent, this.effectivePageSize, 0);
    },
    // 전체 페이지 수 (커버 1 + 초록 n + 본문 n)
    totalPageCount() {
      return 1 + this.abstractPages.length + this.contentPages.length;
    },
    // 페이지별 주석 배분
    splitComments() {
      const comments = this.selectedPaper?.comments || [];
      if (!comments.length || !this.contentPages.length) {
        return this.contentPages.map(() => []);
      }
      // 각 페이지별 주석 배열
      const result = this.contentPages.map(() => []);
      // 각 페이지 콘텐츠에서 주석 마커 찾기
      this.contentPages.forEach((pageContent, pageIndex) => {
        // data-footnote-id 또는 ^숫자) 패턴 찾기
        const markerRegex = /data-footnote-id="(\d+)"|<sup[^>]*>\^?(\d+)\)?<\/sup>/g;
        let match;
        while ((match = markerRegex.exec(pageContent)) !== null) {
          const footnoteId = match[1] || match[2];
          const comment = comments.find(c => {
            const num = c.superscript?.toString().replace(/\D/g, '');
            return num === footnoteId;
          });
          if (comment && !result[pageIndex].includes(comment)) {
            result[pageIndex].push(comment);
          }
        }
      });
      return result;
    },
    coverComments() {
      return this.getCoverCommentsForPaper(this.selectedPaper);
    },
    hasTitlePageComment() {
      return (this.selectedPaper?.comments || []).some((comment) =>
        this.isTitlePageComment(comment)
      );
    },
    fixedPagesList() {
      const base = [
        { id: "cover", title: "표지", page: 1, isBlank: false },
        { id: "inner_front", title: "속표지(앞)", page: 2, isBlank: false },
        { id: "info", title: "정보페이지", page: 3, isBlank: false },
        { id: "title", title: "표제지", page: 4, isBlank: false },
        { id: "toc", title: "목차", page: 5, isBlank: false },
        { id: "blank", title: "빈페이지", page: 6, isBlank: false },
      ];

      try {
        if (this.journal?.fixed_pages_info) {
          const settings = JSON.parse(this.journal.fixed_pages_info);
          return base.filter((page) => settings?.[page.id] !== false);
        }
      } catch (error) {
        console.warn("fixed_pages_info 파싱 실패", error);
      }
      return base;
    },
    pdfItems() {
      if (!this.selectedPaper) return [];
      return [
        {
          ...this.selectedPaper,
          category: this.selectedPaper.category || "paper",
          content: this.htmlContent,
        },
      ];
    },
    pdfGroup() {
      const startPage = this.fixedPagesList.length + 1;
      return { ...(this.selectedGroup || {}), startPage };
    },
    fullPdfItems() {
      return this.fullPdfGroups.reduce((acc, group) => acc.concat(group.contents || []), []);
    },
    singlePaperPages() {
      const pages = [];
      if (!this.selectedPaper) return [];

      // 1. Paper Cover
      pages.push({
        key: `single-cover-${this.selectedPaper.idx}`,
        type: "paper-cover",
        paper: this.selectedPaper,
        pageNumber: this.getSinglePdfPageNumber(1),
        comments: this.coverComments,
        group: this.selectedGroup,
      });

      // 2. Abstract
      this.abstractPages.forEach((html, index) => {
        pages.push({
          key: `single-abstract-${this.selectedPaper.idx}-${index}`,
          type: "abstract",
          paper: this.selectedPaper,
          pageNumber: this.getSinglePdfPageNumber(index + 2),
          html: html,
          isFirst: index === 0,
          isLast: index === this.abstractPages.length - 1,
          group: this.selectedGroup,
        });
      });

      // 3. Content
      this.contentPages.forEach((html, index) => {
        pages.push({
          key: `single-content-${this.selectedPaper.idx}-${index}`,
          type: "paper",
          paper: this.selectedPaper,
          pageNumber: this.getSinglePdfPageNumber(index + this.contentStartPage),
          html: html,
          comments: this.splitComments[index] || [],
          group: this.selectedGroup,
        });
      });

      return pages;
    },
  },
  methods: {
    getAbstractCoverHeight() {
      return this.getAbstractCoverHeightForPaper(this.selectedPaper);
    },
    getAbstractCoverHeightForPaper(paper) {
      if (typeof window === "undefined" || typeof document === "undefined") {
        return 0;
      }

      const contentWidth = getContentWidth(this.effectivePageSize, true);
      const container = document.createElement("div");
      container.style.cssText = `
        position: absolute;
        visibility: hidden;
        width: ${contentWidth}px;
        padding: 0;
      `;

      const titleEl = document.createElement("div");
      titleEl.textContent = "● 초록(Abstract)";
      titleEl.style.cssText = `
        font-size: 12pt;
        font-weight: bold;
        margin: 0 0 16px 0;
      `;
      container.appendChild(titleEl);

      let keywordReserve = 0;
      if (paper?.keyword || paper?.keyword_eng) {
        const keywordWrap = document.createElement("div");
        keywordWrap.style.cssText = `
          font-size: 9pt;
          line-height: 1.4;
        `;
        const keywordLabel = document.createElement("span");
        keywordLabel.textContent = "핵심어";
        keywordLabel.style.cssText = `
          display: inline-block;
          padding: 2px 8px;
          font-size: 9pt;
        `;
        const keywordText = document.createElement("span");
        keywordText.textContent = paper.keyword || paper.keyword_eng || "";
        keywordText.style.cssText = "font-size: 9pt;";
        keywordWrap.appendChild(keywordLabel);
        keywordWrap.appendChild(keywordText);
        container.appendChild(keywordWrap);
      }

      document.body.appendChild(container);
      const titleStyles = window.getComputedStyle(titleEl);
      const titleHeight =
        titleEl.offsetHeight +
        (parseFloat(titleStyles.marginTop) || 0) +
        (parseFloat(titleStyles.marginBottom) || 0);

      if (paper?.keyword || paper?.keyword_eng) {
        const keywordWrap = container.lastChild;
        const keywordHeight = keywordWrap ? keywordWrap.offsetHeight : 0;
        const keywordBottom = 60;
        keywordReserve = keywordHeight + keywordBottom;
      }

      document.body.removeChild(container);
      return Math.ceil(titleHeight + keywordReserve);
    },
    getFixedPagesSettings({ useFixedPagesInfo = true } = {}) {
      if (!useFixedPagesInfo) return {};
      try {
        if (this.journal?.fixed_pages_info) {
          const settings = JSON.parse(this.journal.fixed_pages_info);
          if (settings && typeof settings === "object") {
            return settings;
          }
        }
      } catch (error) {
        console.warn("fixed_pages_info 파싱 실패", error);
      }
      return {};
    },
    isFixedPageEnabled(pageId, { useFixedPagesInfo = true } = {}) {
      const settings = this.getFixedPagesSettings({ useFixedPagesInfo });
      if (Array.isArray(settings)) {
        const frontPageIds = ["cover", "info", "inner_front", "blank", "title", "toc"];
        if (frontPageIds.includes(pageId)) {
          return settings.some((page) => page?.id === pageId);
        }
        return true;
      }
      if (Object.prototype.hasOwnProperty.call(settings, pageId)) {
        return settings[pageId] !== false;
      }
      return true;
    },
    isSingleFixedPageEnabled(pageId) {
      return this.isFixedPageEnabled(pageId, { useFixedPagesInfo: false });
    },
    getFixedFrontPageCount({ useFixedPagesInfo = true } = {}) {
      const frontPageIds = ["cover", "info", "inner_front", "blank", "title", "toc"];
      return frontPageIds.reduce((count, pageId) => (
        count + (this.isFixedPageEnabled(pageId, { useFixedPagesInfo }) ? 1 : 0)
      ), 0);
    },
    getPdfPageNumber(localPageNumber, options = {}) {
      return this.getFixedFrontPageCount(options) + localPageNumber;
    },
    getSinglePdfPageNumber(localPageNumber) {
      return this.getPdfPageNumber(localPageNumber, { useFixedPagesInfo: false });
    },
    adjustPreviewScale() {
      this.$nextTick(() => {
        const previewContent = this.$refs.previewContent;
        if (previewContent) {
          const availableWidth = previewContent.clientWidth - 32;
          const pageWidth = this.dimensions.width;
          this.previewScale = Math.min(availableWidth / pageWidth, 0.6);
        }
      });
    },
    addBlock(type, options = {}) {
      const block = this.createBlock(type, options);
      const insertAt = this.selectedBlockIndex >= 0 ? this.selectedBlockIndex + 1 : this.ast.blocks.length;
      this.ast.blocks.splice(insertAt, 0, block);
      this.selectedBlockIndex = insertAt;
    },
    removeBlock(index) {
      if (index < 0 || index >= this.ast.blocks.length) return;
      this.ast.blocks.splice(index, 1);
      if (this.selectedBlockIndex >= this.ast.blocks.length) {
        this.selectedBlockIndex = this.ast.blocks.length - 1;
      }
    },
    createBlock(type, options = {}) {
      if (type === "heading") return { type, level: 2, text: "" };
      if (type === "paragraph") return { type, text: "" };
      if (type === "blockquote") return { type, text: "", variant: options.variant || 1, title: "" };
      if (type === "table") {
        return {
          type,
          caption: "",
          rowsText: "",
          width: 100,
          layout: "fixed",
          cellPaddingY: 2,
          cellPaddingX: 4,
          lineHeight: 9.5,
          columnWidths: "",
        };
      }
      if (type === "figure") {
        return { type, src: "", caption: "", width: 0, height: 0 };
      }
      if (type === "references") {
        return { type, itemsText: "" };
      }
      if (type === "page_break") return { type };
      return { type: "raw_html", html: "" };
    },
    decodeHtmlEntities(value) {
      if (!value || typeof value !== "string") return value;
      const textarea = document.createElement("textarea");
      textarea.innerHTML = value;
      return textarea.value;
    },
    normalizeAstEntities(ast) {
      if (!ast || !Array.isArray(ast.blocks)) return ast;
      const decode = this.decodeHtmlEntities;
      const blocks = ast.blocks.map((block) => {
        if (!block || !block.type) return block;
        switch (block.type) {
          case "heading":
          case "paragraph":
            return { ...block, text: decode(block.text) };
          case "blockquote":
            return { ...block, text: decode(block.text), title: decode(block.title) };
          case "table":
            return { ...block, caption: decode(block.caption), rowsText: decode(block.rowsText) };
          case "figure":
            return { ...block, src: decode(block.src), caption: decode(block.caption) };
          case "references":
            return { ...block, itemsText: decode(block.itemsText) };
          case "raw_html":
            return { ...block, html: decode(block.html) };
          default:
            return block;
        }
      });
      return { ...ast, blocks };
    },
    insertFootnote(block) {
      if (!block || block.type !== "paragraph") return;
      const matches = block.text.match(/\^(\d+)\)/g);
      const nextNumber = matches ? matches.length + 1 : 1;
      block.text = `${block.text} ^${nextNumber})`.trim();
    },
    processImageUrls(htmlString, { addCrossOrigin = false, preferRelative = false } = {}) {
      if (!htmlString) return htmlString;
      const replaceIskalPath = (match, quote, path) => {
        const normalized = path.startsWith("/") ? path.slice(1) : path;
        if (preferRelative) {
          return `src=${quote}/storage/${normalized}${quote}`;
        }
        if (SERVER_URL) {
          return `src=${quote}${SERVER_URL}/storage/${normalized}${quote}`;
        }
        return match;
      };
      const replaceStorageUrl = (match, quote, path) => {
        const normalized = path.replace(/^\/?storage\//, "");
        if (preferRelative) {
          return `src=${quote}/storage/${normalized}${quote}`;
        }
        if (SERVER_URL) {
          return `src=${quote}${SERVER_URL}/storage/${normalized}${quote}`;
        }
        return match;
      };
      let withAbsolute = htmlString
        .replace(/src=(["'])(\/?iskal\/[^"']+)\1/g, replaceIskalPath)
        .replace(/src=(["'])(https?:\/\/[^"']+\/storage\/[^"']+)\1/g, (match, quote, url) => {
          const [, path = ""] = url.split("/storage/");
          return replaceStorageUrl(match, quote, `/storage/${path}`);
        })
        .replace(/src=(["'])(\/?storage\/[^"']+)\1/g, replaceStorageUrl);
      if (!addCrossOrigin) return withAbsolute;
      return withAbsolute.replace(/<img(?![^>]*\scrossorigin=)/g, '<img crossorigin="anonymous"');
    },
    openGroupSelector() {
      this.showGroupDialog = true;
    },
    async selectGroup(group) {
      this.selectedGroup = group;
      this.showGroupDialog = false;
      await this.fetchPapersByGroup(group.idx);
    },
    async handlePaperChange() {
      if (!this.selectedPaperIdx) {
        this.selectedPaper = null;
        this.ast = { version: 1, blocks: [] };
        this.selectedBlockIndex = -1;
        return;
      }
      try {
        this.isLoading = true;
        const response = await journalContentApi.showJournalContent({ idx: this.selectedPaperIdx });
        if (response.status === 200) {
          this.selectedPaper = response.data.response;
          this.ast = parseContentToAst(this.selectedPaper.content || "");
          this.selectedBlockIndex = this.ast.blocks.length > 0 ? 0 : -1;
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({ message: "논문 정보를 불러오는데 실패했습니다.", style: "danger" });
      } finally {
        this.isLoading = false;
      }
    },
    async savePaper() {
      if (!this.selectedPaper) return;
      try {
        this.isSaving = true;
        const normalizedAst = this.normalizeAstEntities(this.ast);
        this.ast = normalizedAst;
        const data = { ...this.selectedPaper, content: JSON.stringify(normalizedAst) };
        const response = await journalContentApi.updateJournalContent(data);
        if (response.status === 200) {
          this.notifyVue({ message: "저장되었습니다.", style: "success" });
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({ message: "저장에 실패했습니다.", style: "danger" });
      } finally {
        this.isSaving = false;
      }
    },
    exportPdf() {
      const useFullLayout = !!this.$refs.fullPdfContent;
      let previewElement = this.$refs.fullPdfContent || this.$refs.previewContent;
      if (useFullLayout && previewElement) {
        const tempContainer = document.createElement("div");
        tempContainer.innerHTML = this.processImageUrls(previewElement.innerHTML, {
          addCrossOrigin: false,
        });
        previewElement = tempContainer;
      } else if (previewElement) {
        const tempContainer = document.createElement("div");
        tempContainer.innerHTML = this.processImageUrls(previewElement.innerHTML, {
          addCrossOrigin: true,
        });
        previewElement = tempContainer;
      }
      if (!previewElement) {
        this.notifyVue({ message: "미리보기를 먼저 로드해 주세요.", style: "warning" });
        return;
      }
      openIframePrintPreview({
        element: previewElement,
        title: this.selectedPaper?.title || "논문",
        pageSize: "A5",
        usePrint: useFullLayout,
      });
    },
    async exportFullJournalPdf() {
      if (this.groups.length === 0) {
        this.notifyVue({ message: "논문 그룹이 없습니다.", style: "warning" });
        return;
      }

      try {
        this.isFullPdfLoading = true;
        await this.buildFullPdfGroups();
        this.buildFullPdfPages();
        await this.$nextTick();

        const previewElement = this.$refs.fullJournalPdfContent;
        if (!previewElement || this.fullPdfPages.length === 0) {
          this.notifyVue({ message: "전체 PDF를 준비하지 못했습니다.", style: "warning" });
          return;
        }

        const tempContainer = document.createElement("div");
        tempContainer.innerHTML = this.processImageUrls(previewElement.innerHTML, {
          addCrossOrigin: false,
        });

        openIframePrintPreview({
          element: tempContainer,
          title: this.journal?.title || "저널 전체 미리보기",
          pageSize: this.effectivePageSize,
          usePrint: true,
        });
      } catch (error) {
        console.error(error);
        this.notifyVue({ message: "전체 PDF 생성에 실패했습니다.", style: "danger" });
      } finally {
        this.isFullPdfLoading = false;
      }
    },
    async buildFullPdfGroups() {
      const groups = this.groups || [];
      const results = await Promise.all(
        groups.map(async (group) => {
          try {
            const response = await journalContentApi.showContentsByGroup({ group_idx: group.idx });
            if (response.status === 200) {
              return {
                ...group,
                contents: (response.data.response || []).filter(
                  (paper) =>
                    paper.category !== "empty" &&
                    paper.title !== "공페이지" &&
                    paper.title !== "빈페이지"
                ),
              };
            }
          } catch (error) {
            console.error(error);
          }
          return { ...group, contents: [] };
        })
      );
      this.fullPdfGroups = results;
    },
    buildFullPdfPages() {
      const pages = [];
      let pageNumber = 1;

      this.fullPdfGroups.forEach((group, groupIndex) => {
        (group.contents || []).forEach((paper, paperIndex) => {
          const paperData = this.getFullPdfPaperData(paper);
          const abstractHtml = this.buildAbstractHtml(paperData);
          const abstractPages = abstractHtml
            ? splitContentByHeight(
                abstractHtml,
                this.effectivePageSize,
                this.getAbstractCoverHeightForPaper(paperData),
                {
                  containerStyle: 'font-family: "KoPubWorldBatang", "Noto Serif KR", serif; font-size: 10pt; line-height: 1.8; text-align: justify;',
                  paragraphStyle: "margin: 0 0 10px 0;",
                }
              )
            : [""];
          const contentHtml = this.processImageUrls(this.normalizeContentToHtml(paperData?.content));
          const contentPages = contentHtml ? splitContentByHeight(contentHtml, this.effectivePageSize, 0) : [""];
          const splitComments = this.splitCommentsForPaper(paperData, contentPages);

          if (pageNumber % 2 === 0) {
            pages.push({
              key: `paper-${paperData.idx}-blank-${pageNumber}`,
              type: "blank",
              paper: paperData,
              group,
              pageNumber,
            });
            pageNumber += 1;
          }

          this.$set(this.fullPdfGroups[groupIndex].contents, paperIndex, {
            ...paperData,
            start_page: pageNumber,
          });

          pages.push({
            key: `paper-${paperData.idx}-cover`,
            type: "paper-cover",
            paper: paperData,
            group,
            comments: this.getCoverCommentsForPaper(paperData),
            pageNumber,
          });
          pageNumber += 1;

            abstractPages.forEach((pageHtml, index) => {
              pages.push({
                key: `paper-${paperData.idx}-abstract-${index}`,
                type: "abstract",
                paper: paperData,
                group,
                pageNumber,
                html: pageHtml,
                isFirst: index === 0,
                isLast: index === abstractPages.length - 1,
              });
              pageNumber += 1;
            });

          contentPages.forEach((pageHtml, index) => {
            pages.push({
              key: `paper-${paperData.idx}-content-${index}`,
              type: "content",
              paper: paperData,
              group,
              pageNumber,
              html: pageHtml,
              comments: splitComments[index] || [],
            });
            pageNumber += 1;
          });
        });
      });

      if (pages.length > 0) {
        const backPagesCount = ["inner_back", "backcover"].reduce((count, pageId) => (
          count + (this.isFixedPageEnabled(pageId) ? 1 : 0)
        ), 0);
        const lastPageNumber = pageNumber - 1 + backPagesCount;
        if (lastPageNumber % 2 !== 0) {
          pages.push({
            key: `final-blank-${pageNumber}`,
            type: "blank",
            paper: {},
            group: {},
            pageNumber,
          });
          pageNumber += 1;
        }
      }

      this.fullPdfPages = pages;
    },
    getFullPdfPaperData(paper) {
      if (this.selectedPaper && paper?.idx === this.selectedPaper.idx) {
        const contentHtml = renderAstToHtml(this.ast);
        return { ...paper, ...this.selectedPaper, content: contentHtml };
      }
      return paper;
    },
    buildAbstractHtml(paper) {
      const source = paper?.abstract || paper?.abstract_eng || "";
      if (!source) return "";
      return this.processImageUrls(renderAstToHtml(parseContentToAst(source)));
    },
    splitCommentsForPaper(paper, contentPages) {
      const comments = paper?.comments || [];
      if (!comments.length || !contentPages.length) {
        return contentPages.map(() => []);
      }
      const result = contentPages.map(() => []);
      contentPages.forEach((pageContent, pageIndex) => {
        const markerRegex = /data-footnote-id="(\d+)"|<sup[^>]*>\^?(\d+)\)?<\/sup>/g;
        let match;
        while ((match = markerRegex.exec(pageContent)) !== null) {
          const footnoteId = match[1] || match[2];
          const comment = comments.find(c => {
            const num = c.superscript?.toString().replace(/\D/g, "");
            return num === footnoteId;
          });
          if (comment && !result[pageIndex].includes(comment)) {
            result[pageIndex].push(comment);
          }
        }
      });
      return result;
    },
    getCoverCommentsForPaper(paper) {
      const comments = paper?.comments || [];
      if (!comments.length) return [];
      const coverTextParts = [
        paper?.title,
        paper?.title_eng,
      ];
      (paper?.authors || []).forEach((author) => {
        coverTextParts.push(author?.full_name);
        coverTextParts.push(author?.name_eng);
        coverTextParts.push(author?.organization);
        coverTextParts.push(author?.organization_eng);
      });
      const coverText = coverTextParts.filter(Boolean).join(" ");
      const markerComments = this.collectCommentsByMarkers(coverText, comments);
      const titlePageComments = comments.filter((comment) =>
        this.isTitlePageComment(comment)
      );
      if (!titlePageComments.length) return markerComments;
      const combined = [...markerComments];
      titlePageComments.forEach((comment) => {
        if (!combined.includes(comment)) {
          combined.push(comment);
        }
      });
      return combined;
    },
    collectCommentsByMarkers(text, comments) {
      if (!text || !comments?.length) return [];
      const markerRegex = /data-footnote-id="(\d+)"|<sup[^>]*>\^?(\d+)\)?<\/sup>|\^(\d+)/g;
      const result = [];
      const used = new Set();
      let match;
      while ((match = markerRegex.exec(text)) !== null) {
        const footnoteId = match[1] || match[2] || match[3];
        const comment = comments.find((c) => {
          const num = c.superscript?.toString().replace(/\D/g, "");
          return num === footnoteId;
        });
        if (comment && !used.has(comment.idx)) {
          used.add(comment.idx);
          result.push(comment);
        }
      }
      return result;
    },
    isTitlePageComment(comment) {
      if (!comment) return false;
      if (comment.page === "title_page") return true;
      return comment.superscript === "*" && Number(comment.page) === 1;
    },
    normalizeContentToHtml(rawContent) {
      if (!rawContent) return "";
      if (typeof rawContent !== "string") {
        return rawContent;
      }
      const trimmed = rawContent.trim();
      if (!trimmed) return "";
      if (trimmed.startsWith("{")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed && parsed.version && Array.isArray(parsed.blocks)) {
            return renderAstToHtml(parsed);
          }
        } catch (error) {
          console.warn("content JSON parse failed", error);
        }
      }
      return rawContent;
    },
    async fetchJournal() {
      try {
        const response = await journalApi.showJournal({ idx: this.journal_idx });
        if (response.status === 200) {
          this.journal = response.data.response;
        }
      } catch (error) {
        console.error(error);
      }
    },
    async fetchGroups() {
      try {
        const response = await journalContentGroupApi.showAll({ journal_idx: this.journal_idx });
        if (response.status === 200) {
          this.groups = response.data.response;
          if (this.groups.length > 0) {
            this.selectedGroup = this.groups[0];
            await this.fetchPapersByGroup(this.groups[0].idx);
          }
        }
      } catch (error) {
        console.error(error);
      }
    },
    async fetchPapersByGroup(groupIdx) {
      try {
        const response = await journalContentApi.showContentsByGroup({ group_idx: groupIdx });
        if (response.status === 200) {
          const allPapers = response.data.response || [];
          this.papers = allPapers.filter(
            (paper) => paper.category !== "empty" && paper.title !== "공페이지" && paper.title !== "빈페이지"
          );
        }
      } catch (error) {
        console.error(error);
      }
    },
    notifyVue({ message = "", style = "primary" } = {}) {
      this.$notify({ timeout: 5000, message, icon: "priority_high", type: style, horizontalAlign: "right", verticalAlign: "top" });
    },
  },
  async mounted() {
    await this.fetchJournal();
    await this.fetchGroups();
    this.adjustPreviewScale();
    window.addEventListener("resize", this.adjustPreviewScale);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.adjustPreviewScale);
  },
};
</script>

<style scoped>
/* 에디터 레이아웃 스타일만 유지 */
.paper-editor-wrapper {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 100px);
  min-height: 700px;
  background-color: #f5f5f5;
}

.paper-editor-header {
  padding: 12px 20px;
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.paper-editor-body {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.editor-panel {
  width: 50%;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e0e0e0;
  background-color: white;
}

.paper-info-bar {
  padding: 12px 20px;
  border-bottom: 1px solid #f0f0f0;
  background-color: #fafafa;
}

.editor-toolbar {
  padding: 8px 16px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.editor-blocks {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: white;
}

.editor-block {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  position: relative;
  transition: all 0.2s;
}

.editor-block:hover {
  border-color: #1976d2;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.1);
}

.editor-block-active {
  border-color: #1976d2;
  border-width: 2px;
}

.block-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.block-type {
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;
  color: #9e9e9e;
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
}

.block-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.block-body-center {
  align-items: center;
  padding: 10px;
  background-color: #fafafa;
  border-radius: 4px;
}

.table-settings, .figure-settings {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.block-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}

.editor-empty {
  text-align: center;
  padding: 40px;
  color: #9e9e9e;
  font-style: italic;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
}
</style>

<style>
/* ========== 본문 콘텐츠 스타일 (ArticlePaperPage A5 기준) ========== */
.paper-content {
  font-family: "KoPubWorldBatang", "Noto Serif KR", serif;
}

.paper-content p {
  font-size: 9pt;
  line-height: 15pt;
  text-align: justify;
  text-indent: 1em;
  margin: 6px 0;
  word-break: break-all;
}

.paper-content h2 {
  font-size: 14pt;
  line-height: 20pt;
  font-weight: 700;
  margin: 20px 0 10px;
  padding-bottom: 4px;
  border-bottom: 1.5px solid #333;
}

.paper-content h3 {
  font-size: 11.5pt;
  line-height: 20pt;
  font-weight: 700;
  font-family: "KoPubWorldDotum", "Noto Sans KR", sans-serif;
  margin: 16px 0 8px;
}

.paper-content h4 {
  font-size: 10pt;
  line-height: 18pt;
  font-weight: 600;
  margin: 12px 0 6px;
}

.paper-content h5,
.paper-content h6 {
  font-size: 9pt;
  line-height: 15pt;
  font-weight: 600;
  margin: 10px 0 4px;
}

.paper-content strong {
  font-weight: 700;
}

.paper-content em {
  font-style: italic;
}

.paper-content sup {
  font-size: 0.8em;
  vertical-align: super;
}

.paper-content ul,
.paper-content ol {
  margin: 8px 0 8px 20px;
  font-size: 9pt;
}

.paper-content li {
  margin: 4px 0;
  line-height: 15pt;
}

/* 인용문 스타일 (A5) */
.paper-content .blockquote,
.paper-content blockquote,
.paper-content .doc-blockquote {
  margin: 12px 0;
  padding: 10px 14px;
  background: #f9fafb;
  border-left: 3px solid #9ca3af;
  font-size: 8pt!important;
  line-height: 13pt;
  color: #4b5563;
  text-indent: 0;
}

/* 표 스타일 (A5) */
.paper-content table {
  width: 100% !important;
  max-width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  border: 1px solid #333;
  font-family: "KoPubWorldDotum", "Noto Sans KR", sans-serif;
  font-size: 7pt;
  line-height: 9.5pt;
  margin: 14px 0;
}

.paper-content table caption {
  padding: 8px;
  caption-side: top;
  text-align: center;
  font-size: 8.5pt;
  line-height: 16.7pt;
  font-weight: 400;
}

.paper-content th,
.paper-content td {
  padding: 2px 4px;
  border: 1px solid #333;
  text-align: center;
  word-break: break-word;
  line-height: 9.5pt;
}

.paper-content thead th {
  background-color: #333;
  color: #fff;
  font-size: 8pt;
  line-height: 10.5pt;
  font-weight: 400;
  text-align: center;
}

.paper-content thead td,
.paper-content tr:first-child th,
.paper-content tr:first-child td {
  text-align: center;
}

.paper-content .page-break {
  display: none;
}

/* 참고문헌 스타일 (A5) */
.paper-content .references-title {
  margin-top: 30px;
  font-size: 11pt;
  font-weight: 700;
  margin-bottom: 12px;
}

.paper-content .reference-item {
  margin: 4px 0;
  padding-left: 1.5em;
  text-indent: -1.5em;
  font-size: 8pt;
  line-height: 12pt;
  page-break-inside: avoid;
  break-inside: avoid;
}

/* 그림 스타일 (A5) */
.paper-content .figure-container {
  margin: 0px 0;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.paper-content .figure-container .figure-image-wrapper {
  display: inline-block;
  padding: 12px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
}

.paper-content .figure-container .figure-image-wrapper img {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}

.paper-content .figure-container > img {
  display: inline-block;
  max-width: calc(100% - 28px);
  height: auto;
  padding: 12px;
  border: 2px dashed #d1d5db !important;
  border-radius: 8px !important;
  box-shadow: none !important;
}

.paper-content .figure-container .figure-placeholder {
  display: inline-block;
  width: 80%;
  max-width: 350px;
  height: 150px;
  background: #f3f4f6;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
}

.paper-content .figure-container .figure-caption {
  order: -1;
  margin-top: 0;
  margin-bottom: 6px;
  font-family: "KoPubWorldDotum", "Noto Sans KR", sans-serif;
  font-size: 8.5pt;
  line-height: 16.7pt;
  font-weight: 400;
  text-align: center;
}

.paper-content .figure-container.figure-caption-bottom .figure-caption {
  order: 0;
  margin-top: 6px;
  margin-bottom: 0;
}

/* 인용 박스 스타일 (box-style) */
.paper-content .box-style1,
.paper-content .box-style2,
.paper-content .box-style3,
.paper-content .box-style4 {
  margin: 8px 0;
}

.paper-content .box-style1-title .box-style1-title-text {
  display: inline-flex;
  margin-left: 28px;
  border-radius: 14px 14px 0 0;
  background-color: #e2e3e5;
  padding: 4px 12px;
  font-size: 8.5pt;
  color: #2f2f2f;
}

.paper-content .box-style1-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  border: 1.5px solid #cfd3d7;
  border-radius: 14px;
  padding: 14px;
  width: 100%;
  background-color: #fafafa;
}

.paper-content .box-style1-list-items {
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
}

.paper-content .box-style1-list-item {
  border-bottom: 1px solid #e1e4e8;
  padding: 1px 0;
  margin: 0;
}

.paper-content .box-style1-list-item:last-child {
  border-bottom: none;
}

.paper-content .box-style1-list-item p,
.paper-content .box-style1-list-item div,
.paper-content .box-style1-list-item span {
  margin: 0 !important;
  text-indent: 0 !important;
  line-height: 12pt;
  font-size: 8pt;
}

.paper-content .box-style2-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1.5px dotted #9aa0a6;
  border-radius: 10px;
  background-color: #f8f9fb;
  padding: 10px;
  width: 100%;
}

.paper-content .box-style2-card-title {
  display: inline-flex;
  border: 1px solid #9aa0a6;
  border-radius: 10px;
  background-color: #f5f6f8;
  padding: 0 8px;
  width: fit-content;
  color: #4b4f55;
  font-size: 8.5pt;
}

.paper-content .box-style2-card-text {
  color: #2f2f2f;
  line-height: 1.6;
  text-indent: 0;
  font-size: 8.5pt;
}

.paper-content .box-style3 {
  border: 1px solid #d2d6db;
  border-left: 5px solid #8b9096;
  border-radius: 12px;
  background-color: #f6f7f9;
  padding: 10px 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.paper-content .box-style3-header {
  margin-bottom: 4px;
  font-size: 8.5pt;
  color: #2f2f2f;
}

.paper-content .box-style3-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  list-style: none;
  padding: 0;
  margin: 0;
}

.paper-content .box-style3-list p {
  margin: 0;
  font-size: 8.5pt;
}

.paper-content .box-style4 {
  border: 1px solid #d2d5da;
  border-radius: 14px;
  background: linear-gradient(180deg, #f6f7f9 0%, #ffffff 100%);
  padding: 10px 12px;
}

.paper-content .box-style4-head {
  font-size: 8.5pt;
  margin-bottom: 4px;
  color: #2f2f2f;
}

.paper-content .box-style4-body p {
  color: #2f2f2f;
  line-height: 1.6;
  font-size: 8.5pt;
}

/* 이미지 공통 스타일 */
.paper-content img {
  border: none !important;
  border-radius: 0 !important;
  padding: 0 !important;
  box-shadow: none !important;
}
</style>
